/**
 * @fileoverview Own staff editing and explicit send/skip/follow-up actions for recorded Intake outreach. `intakeOutreach.js` supplies draft/policy presentation and `intakeApi.js` owns request boundaries. Backend delivery policy and current lead state govern accepted sends. Displaying a draft, refreshing delivery status, and delivering email remain separate operations.
 */

import React, { useContext, useEffect, useRef, useState } from 'react';
import ToastContext from '../../ToastContext';
import AppIcon from '../shared/AppIcon';
import { EmptyState, SelectInput, TextArea, TextInput } from './IntakeDetailPrimitives';
import { createIntakeOutreach, fetchIntakeOutreach, sendIntakeOutreach, updateIntakeOutreach } from './intakeApi';
import { EDITABLE_OUTREACH_STATUSES, SEND_BLOCKED_OUTREACH_STATUSES } from './intakeStatuses';
import { buildOutreachForm, buildOutreachPayload, getDefaultOutreachForm, getDraftSubtitle, getOutreachDeliveryNotice, getOutreachSendToast } from './intakeOutreach';



function WorkflowBadge({ status }) {
  return <span className={`intake-badge intake-badge--${String(status || 'draft').toLowerCase()}`}>{String(status || 'draft').replaceAll('_', ' ')}</span>;
}

// This is deliberately shared by the pipeline and the Inbox task surface: a
// request-documents task should expose the same draft, save, and send workflow.


export default function OutreachWorkspace({ lead, onLeadUpdated, onRefreshLead }) {
  const showToast = useContext(ToastContext);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTarget, setSendingTarget] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [deliveryPolicy, setDeliveryPolicy] = useState(null);
  const [form, setForm] = useState(() => getDefaultOutreachForm(lead));
  const selectedIdRef = useRef(null);

  const selectedRow = rows.find((row) => row.id === selectedId);
  const isNewDraft = selectedId === 'new';
  const editorActive = isNewDraft || selectedRow;
  const editorLocked = selectedRow && !EDITABLE_OUTREACH_STATUSES.has(selectedRow.status);
  const draftRows = rows.filter((row) => row.message_type === 'email' && EDITABLE_OUTREACH_STATUSES.has(row.status));
  const requiredSendFieldsPresent = Boolean(form.recipient_email && form.subject && form.body);
  const deliveryNotice = getOutreachDeliveryNotice(deliveryPolicy, form.recipient_email);
  const deliveryMode = String(deliveryPolicy?.mode || '').toLowerCase();
  const isDryRun = deliveryMode === 'dry_run';
  const isLiveDelivery = deliveryPolicy?.live === true || deliveryMode === 'live';
  const devDeliveryTarget = deliveryPolicy?.redirect_email || (String(deliveryPolicy?.mode || '').toLowerCase() === 'redirect' ? deliveryPolicy?.delivered_recipient_email : '');
  const canSendBase = Boolean(editorActive) && form.message_type === 'email' && requiredSendFieldsPresent && !editorLocked && !SEND_BLOCKED_OUTREACH_STATUSES.has(selectedRow?.status) && !saving && !sendingTarget;
  const formLead = { prospect_name: lead?.prospect_name, prospect_email: lead?.prospect_email, prospect_phone: lead?.prospect_phone };

  useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);
  useEffect(

  () => {
    let active = true;
    setLoading(true);
    setRows([]);
    setSelectedId(null);
    setForm(getDefaultOutreachForm(formLead));
    fetchIntakeOutreach(lead.id).then(

    (response) => {
      if (!active) return;
      const outreach = response.data?.outreach || [];
      setRows(outreach);
      setDeliveryPolicy(response.data?.delivery_policy || null);
      const defaultRow = outreach.find((row) => row.message_type === 'email' && EDITABLE_OUTREACH_STATUSES.has(row.status));
      if (defaultRow) {
        selectedIdRef.current = defaultRow.id;
        setSelectedId(defaultRow.id);
        setForm(buildOutreachForm(formLead, defaultRow));
      }
    }).catch(() => {
      if (active) showToast({ severity: 'error', summary: 'Outreach unavailable', detail: 'Failed to load outreach drafts.' });
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [lead.id]); // The task always supplies one stable lead.

  const mergeSavedRow =

  (savedRow, select = true) => {
    setRows((current) => current.some((row) => row.id === savedRow.id) ? current.map((row) => row.id === savedRow.id ? savedRow : row) : [savedRow, ...current]);
    if (select) {
      setSelectedId(savedRow.id);
      setForm(buildOutreachForm(formLead, savedRow));
    }
  };
  const startNewDraft =

  () => { setSelectedId('new'); setForm(getDefaultOutreachForm(formLead)); };
  const selectRow =

  (row) => { setSelectedId(row.id); setForm(buildOutreachForm(formLead, row)); };
  const saveCurrentDraft =

  async (silent = false) => {
    if (!editorActive || editorLocked) return null;
    setSaving(true);
    try {
      const response = selectedRow ? await updateIntakeOutreach(lead.id, selectedRow.id, buildOutreachPayload(form)) : await createIntakeOutreach(lead.id, buildOutreachPayload(form));
      if (response.data?.delivery_policy) setDeliveryPolicy(response.data.delivery_policy);
      mergeSavedRow(response.data.outreach);
      if (!silent) showToast({ severity: 'success', summary: 'Saved', detail: 'Outreach draft saved.' });
      return response.data.outreach;
    } catch (error) {
      showToast({ severity: 'error', summary: 'Draft not saved', detail: error?.response?.data?.error || 'Failed to save outreach draft.' });
      return null;
    } finally { setSaving(false); }
  };
  const sendCurrentDraft =

  async (sendTarget) => {
    if (!canSendBase || (sendTarget === 'dev' && !devDeliveryTarget)) return;
    const savedRow = await saveCurrentDraft(true);
    if (!savedRow) return;
    setSendingTarget(sendTarget);
    try {
      const response = await sendIntakeOutreach(lead.id, savedRow.id, { send_target: sendTarget });
      const sentRow = response.data.outreach;
      if (response.data?.delivery_policy) setDeliveryPolicy(response.data.delivery_policy);
      mergeSavedRow(sentRow, false);
      setSelectedId(null);
      setForm(getDefaultOutreachForm(formLead));
      if (response.data?.lead) onLeadUpdated?.(response.data.lead);
      else onRefreshLead?.();
      showToast(getOutreachSendToast(response.data?.delivery_policy || response.data?.delivery || deliveryPolicy, form.recipient_email));
    } catch (error) {
      showToast({ severity: 'error', summary: 'Outreach not sent', detail: error?.response?.data?.error || 'Failed to send outreach email.' });
    } finally { setSendingTarget(null); }
  };
  const skipCurrentDraft =

  async () => {
    if (!selectedRow || editorLocked) return;
    setSaving(true);
    try {
      const response = await updateIntakeOutreach(lead.id, selectedRow.id, { status: 'skipped' });
      mergeSavedRow(response.data.outreach, false);
      setSelectedId(null);
      setForm(getDefaultOutreachForm(formLead));
      showToast({ severity: 'success', summary: 'Skipped', detail: 'Outreach draft skipped.' });
    } catch (error) {
      showToast({ severity: 'error', summary: 'Draft not updated', detail: error?.response?.data?.error || 'Failed to skip outreach draft.' });
    } finally { setSaving(false); }
  };

  return <section className="intake-pipeline-control task-inbox__outreach-workspace" aria-label="Outreach workspace">
    <div className="intake-section-heading"><div><h3>Outreach</h3></div><div className="intake-action-row"><button type="button" className="intake-button" onClick={startNewDraft}><AppIcon icon="pi pi-plus" aria-hidden="true" /><span>New Draft</span></button></div></div>
    {loading ? <EmptyState title="Loading drafts…" /> : <>
      {draftRows.length ? <div className="intake-draft-queue" aria-label="Email draft queue">{draftRows.map((row) => <button type="button" className={`intake-draft-card${row.id === selectedId ? ' intake-draft-card--selected' : ''}`} key={row.id} onClick={() => selectRow(row)}><div className="intake-draft-card__main"><strong>{row.subject || 'Email draft'}</strong><span>{row.recipient_email || row.recipient_name || '-'}</span><small>{getDraftSubtitle(row)}</small></div><WorkflowBadge status={row.status} /></button>)}</div> : null}
      {editorActive ? <form className="intake-draft-editor" onSubmit={(event) => { event.preventDefault(); saveCurrentDraft(); }}>
        <div className="intake-form-grid">
          <SelectInput label="Type" value={form.message_type} disabled={editorLocked || saving || sendingTarget} onChange={(value) => setForm((current) => ({ ...current, message_type: value }))} options={[["email", "Email"], ["sms", "SMS record"], ["phone", "Phone record"], ["internal", "Internal note"]]} />
          <TextInput label="Recipient" value={form.recipient_name} disabled={editorLocked || saving || sendingTarget} onChange={(value) => setForm((current) => ({ ...current, recipient_name: value }))} />
          <TextInput label="To" value={form.recipient_email} disabled={editorLocked || saving || sendingTarget} onChange={(value) => setForm((current) => ({ ...current, recipient_email: value }))} />
          <TextInput label="Phone" value={form.recipient_phone} disabled={editorLocked || saving || sendingTarget} onChange={(value) => setForm((current) => ({ ...current, recipient_phone: value }))} />
          <TextInput label="Subject" value={form.subject} disabled={editorLocked || saving || sendingTarget} onChange={(value) => setForm((current) => ({ ...current, subject: value }))} />
          <TextArea label="Requested Info" value={form.requested_info} rows={3} disabled={editorLocked || saving || sendingTarget} onChange={(value) => setForm((current) => ({ ...current, requested_info: value }))} />
          <TextArea label="Message" value={form.body} rows={8} disabled={editorLocked || saving || sendingTarget} onChange={(value) => setForm((current) => ({ ...current, body: value }))} />
        </div>
        {deliveryNotice && form.message_type === 'email' ? <div className={`intake-draft-delivery-notice intake-draft-delivery-notice--${deliveryNotice.tone}`}><AppIcon icon={deliveryNotice.icon} aria-hidden="true" /><div><strong>{deliveryNotice.title}</strong><span>{deliveryNotice.detail}</span></div></div> : null}
        {selectedRow?.send_error ? <div className="intake-draft-editor__error">{selectedRow.send_error}</div> : null}
        <div className="intake-draft-editor__actions">
          <button type="submit" className="intake-button" disabled={saving || sendingTarget || editorLocked}><AppIcon icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-save'} aria-hidden="true" /><span>{saving ? 'Saving' : 'Save Draft'}</span></button>
          {devDeliveryTarget && <button type="button" className="intake-button" disabled={!canSendBase} onClick={() => sendCurrentDraft('dev')}><AppIcon icon={sendingTarget === 'dev' ? 'pi pi-spin pi-spinner' : 'pi pi-send'} aria-hidden="true" /><span>{sendingTarget === 'dev' ? 'Sending' : 'Send to Dev Account'}</span></button>}
          {(isLiveDelivery || isDryRun) && <button type="button" className="intake-button intake-button--primary" disabled={!canSendBase} onClick={() => sendCurrentDraft('actual')}><AppIcon icon={sendingTarget === 'actual' ? 'pi pi-spin pi-spinner' : 'pi pi-send'} aria-hidden="true" /><span>{sendingTarget === 'actual' ? 'Sending' : (isDryRun ? 'Record Dry Run' : 'Send Email')}</span></button>}
          <button type="button" className="intake-button" disabled={!selectedRow || saving || sendingTarget || editorLocked} onClick={skipCurrentDraft}><AppIcon icon="pi pi-ban" aria-hidden="true" /><span>Skip</span></button>
        </div>
      </form> : <div className="intake-draft-empty-action"><div><strong>No open email drafts</strong><span>Create a draft for this outreach.</span></div><button type="button" className="intake-button intake-button--primary" onClick={startNewDraft}><AppIcon icon="pi pi-plus" aria-hidden="true" /><span>New Draft</span></button></div>}
    </>}
  </section>;
}
